import { ApplicationConfig, provideBrowserGlobalErrorListeners, provideZoneChangeDetection } from '@angular/core';
import { provideRouter } from '@angular/router';
import { provideAnimations } from '@angular/platform-browser/animations';
import { HTTP_INTERCEPTORS, provideHttpClient, withInterceptors, withInterceptorsFromDi } from '@angular/common/http';
import { MAT_DATE_LOCALE, provideNativeDateAdapter } from '@angular/material/core';
import {
  PublicClientApplication,
  InteractionType,
  BrowserCacheLocation,
} from '@azure/msal-browser';
import {
  MsalService,
  MsalGuard,
  MsalBroadcastService,
  MsalInterceptor,
  MSAL_INSTANCE,
  MSAL_GUARD_CONFIG,
  MSAL_INTERCEPTOR_CONFIG,
} from '@azure/msal-angular';

import { routes } from './app.routes';
import { apiInterceptor } from './core/common/api/api-interceptor';
import { environment } from '../environments/environment';
import { allApiScopes } from '../environments/environment.type';

// --- MSAL Instance (wird in main.ts vor dem Bootstrap initialisiert) ---
export const msalInstance = new PublicClientApplication({
  auth: {
    clientId: environment.msalConfig.clientId,
    authority: environment.msalConfig.authority,
    redirectUri: environment.msalConfig.redirect,
    postLogoutRedirectUri: environment.msalConfig.redirect,
  },
  cache: {
    cacheLocation: BrowserCacheLocation.LocalStorage,
  },
});

// --- Protected Resource Map ---
export function protectedResourceMapFactory(): Map<string, string[]> {
  const map = new Map<string, string[]>();
  for (const scope of allApiScopes(environment.apiScopes)) {
    map.set(scope.uri, scope.scopes);
  }
  return map;
}

const resourceScopes = Array.from(protectedResourceMapFactory().values()).flat();
const initialScopes = Array.from(new Set(['openid', 'profile', 'email', ...resourceScopes]));

export const appConfig: ApplicationConfig = {
  providers: [
    provideBrowserGlobalErrorListeners(),
    provideZoneChangeDetection({ eventCoalescing: true }),
    provideRouter(routes),
    provideAnimations(),

    // HTTP: funktionale Interceptors (apiInterceptor für Mocks) + DI-basierte (MsalInterceptor)
    provideHttpClient(
      withInterceptors([apiInterceptor]),
      withInterceptorsFromDi(),
    ),

    // --- MSAL Providers ---
    {
      provide: MSAL_INSTANCE,
      useValue: msalInstance,
    },
    {
      provide: MSAL_GUARD_CONFIG,
      useValue: {
        interactionType: InteractionType.Redirect,
        authRequest: {
          scopes: initialScopes,
        },
      },
    },
    {
      provide: MSAL_INTERCEPTOR_CONFIG,
      useFactory: () => ({
        interactionType: InteractionType.Redirect,
        protectedResourceMap: protectedResourceMapFactory(),
      }),
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: MsalInterceptor,
      multi: true,
    },
    MsalService,
    MsalGuard,
    MsalBroadcastService,

    // --- Material ---
    { provide: MAT_DATE_LOCALE, useValue: 'de-DE' },
    provideNativeDateAdapter(),
  ],
};
